import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";
import * as yup from "yup";
import { RootState } from "../../../../redux";
import AlertMessage from "../../../../ui/components/alert-message/alert-message";
import CustomInput from "../../../../ui/components/custom-input/custom-input";
import CustomModal from "../../../../ui/components/custom-modal/custom-modal";
import TextArea from "../../../../ui/components/text-area/text-area";
import { UserInfo } from "../../../../ui/models/userInfo";
import { ListModel } from "../../../models/list-model";
import { Practica } from "../../../models/practica";
import { PracticaEditAdmin } from "../../../models/practica-edit-admin";
import { practicaServices } from "../../../services/practica.services";
import { getFormattedDate, getFormattedDecimal } from "../../../utils/common.utils";
import { handle400Error } from "../../../utils/handle-400-errors";
import "./practica-edit-admin-modal.scss";

export interface PracticaEditAdminModalProps {
  show: boolean;
  practicaId: string;
  handleClose: (success: boolean, item: any) => void;
  refetch: () => void;
}

export function PracticaEditAdminModal(props: PracticaEditAdminModalProps) {
  const { show, practicaId, handleClose, refetch } = props;
  const [practica, setPractica] = useState<Practica>();
  const [formTipos] = useState<ListModel[]>([
    { value: "3", label: "Sin Marcas" },
    { value: "4", label: "Planes Especiales" },
    { value: "44", label: "Internado" },
    { value: "5", label: "Hemodiálisis" },
  ]);
  const [showAlertMessage, setShowAlertMessage] = useState<boolean>(false);
  const [textAlertMessage, setTextAlertMessage] = useState<string>();
  const schema = yup
    .object()
    .shape({
      id: yup.string().trim().required("Campo requerido"),
      observacion: yup.string().trim().max(250, "250 Máximo"),
      cantidad: yup
        .number()
        .typeError("Debe ser un número")
        .min(1, "1 Mínimo")
        .max(99, "99 Máximo")
        .required("Campo requerido"),
    })
    .required();

  const {
    register,
    getFieldState,
    formState,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
  } = useForm<PracticaEditAdmin>({
    mode: "all",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (practicaId)
      practicaServices
        .getById(practicaId)
        .then((response) => {
          setPractica(response.data);
        })
        .catch();
  }, [practicaId]);

  const handleSubmitGuardar = (data: any) => {
    practicaServices
      .editByAdmin(data)
      .then((response: any) => {
        refetch();
        handleClose(true, response.data);
      })
      .catch((error: any) => {
        if (error?.response?.status === 400) {
          handle400Error(error.response.data.errors, setError);
        } else {
          setTextAlertMessage(
            "No se pudo editar la práctica. Intente nuevamente."
          );
          setShowAlertMessage(true);
        }
      });

    props.handleClose(true, data);
  };

  const handleSubmitEnviar = (data: any) => {
    practicaServices
      .Enviar(data)
      .then((response: any) => {
        refetch();
        handleClose(true, response.data);
      })
      .catch((error: any) => {
        if (error?.response?.status === 400) {
          handle400Error(error.response.data.errors, setError);
        } else {
          setTextAlertMessage(
            "No se pudo editar la práctica. Intente nuevamente."
          );
          setShowAlertMessage(true);
        }
      });

    props.handleClose(true, data);
  };

  const handleSubmitRechazar = (data: any) => {
    practicaServices
      .Rechazar(data)
      .then((response: any) => {
        refetch();
        handleClose(true, response.data);
      })
      .catch((error: any) => {
        if (error?.response?.status === 400) {
          handle400Error(error.response.data.errors, setError);
        } else {
          setTextAlertMessage(
            "No se pudo editar la práctica. Intente nuevamente."
          );
          setShowAlertMessage(true);
        }
      });

    props.handleClose(true, data);
  };

  const ActionsComponent = () => {
    return (
      <div className="button-edit-admin-action">
        <div className="left">
          <Button
            className="create-button"
            variant="outlined"
            color="error"
            onClick={handleSubmit((data) => handleSubmitRechazar(data))}
            disabled={formState.disabled}
          >
            Rechazar
          </Button>
          <Button
            className="create-button"
            variant="outlined"
            color="success"
            onClick={handleSubmit((data) => handleSubmitEnviar(data))}
            disabled={formState.disabled}
          >
            Enviar
          </Button>
        </div>
        <div className="right">
          <Button
            className="close-button"
            onClick={() => handleClose(false, null)}
          >
            Cancelar
          </Button>
          <Button
            className="create-button"
            variant="contained"
            onClick={handleSubmit((data) => handleSubmitGuardar(data))}
            disabled={formState.disabled}
          >
            Guardar
          </Button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (practica) {
      setValue("id", practica.id, {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("cantidad", practica.cantidad, {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      });
      if (practica.observacion !== "")
        setValue("observacion", practica.observacion, {
          shouldTouch: true,
          shouldDirty: true,
          shouldValidate: true,
        });
    }
  }, [practica]);

  const authInfo = useSelector<RootState, UserInfo>(
    ({ auth }: any) => auth.info,
    shallowEqual
  );

  const searchFormTipo = (formTipo: string) => {
    const tipo = formTipos.filter((f) => f.value === formTipo);
    if (tipo.length !== 0) return tipo[0].label;

    return "-";
  };

  return (
    <CustomModal
      show={show}
      handleClose={() => handleClose(false, null)}
      title="Modificar Práctica"
      actions={<ActionsComponent />}
      size="lg"
    >
      <div className="practica-edit-admin-modal">
        <Box
          sx={
            practica?.practicaSempre
              ? { height: 600, flexGrow: 1 }
              : { height: 320, flexGrow: 1 }
          }
          component="form"
          autoComplete="off"
        >
          <div className="practica-edit-container">
            <AlertMessage
              text={textAlertMessage ? textAlertMessage : ""}
              type={"error"}
              handleShowExternaly={true}
              show={showAlertMessage}
              setShow={setShowAlertMessage}
            />
            <Grid
              container
              spacing={2}
              rowSpacing={4}
              sx={{ marginBottom: "20px" }}
            >
              <Grid size={6}>
                <Typography
                  variant="subtitle2"
                  component="h2"
                  sx={{ fontWeight: 900 }}
                >
                  Profesional
                </Typography>
                <Typography variant="subtitle1" component="text">
                  {practica?.profesionalDescripcion}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              rowSpacing={4}
              sx={{ marginBottom: "20px" }}
            >
              <Grid size={3}>
                <Typography
                  variant="subtitle2"
                  component="h2"
                  sx={{ fontWeight: 900 }}
                >
                  Fecha
                </Typography>
                <Typography variant="subtitle1" component="text">
                  {practica?.fecha
                    ? getFormattedDate(practica?.fecha.toString())
                    : "-"}
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography
                  variant="subtitle2"
                  component="h2"
                  sx={{ fontWeight: 900 }}
                >
                  Obra Social
                </Typography>
                <Typography variant="subtitle1" component="text">
                  {practica?.obraSocialNombre}
                </Typography>
              </Grid>
              <Grid size={5}>
                <Typography
                  variant="subtitle2"
                  component="h2"
                  sx={{ fontWeight: 900 }}
                >
                  Paciente
                </Typography>
                <Typography variant="subtitle1" component="text">
                  {practica?.paciente}
                </Typography>
              </Grid>

              <Grid size={6}>
                <Typography
                  variant="subtitle2"
                  component="h2"
                  sx={{ fontWeight: 900 }}
                >
                  Práctica
                </Typography>
                <Typography variant="subtitle1" component="text">
                  {practica?.practicaCodigo +
                    " - " +
                    practica?.practicaDescripcion}
                </Typography>
              </Grid>
              <Grid size={3}>
                <Typography
                  variant="subtitle2"
                  component="h2"
                  sx={{ fontWeight: 900 }}
                >
                  Precio
                </Typography>
                <Typography variant="subtitle1" component="text">
                  {practica?.precio &&
                    getFormattedDecimal(practica?.precio)}
                </Typography>
              </Grid>
              <Grid size={3}>
                <CustomInput
                  name="cantidad"
                  label="Cantidad"
                  type="number"
                  register={register}
                  getFieldState={getFieldState}
                  formState={formState}
                  errors={errors}
                  required={true}
                  max={90}
                  min={1}
                ></CustomInput>
              </Grid>
            </Grid>

            {practica?.practicaSempre && (
              <Grid
                container
                spacing={2}
                rowSpacing={4}
                sx={{ marginTop: "20px", marginBottom: "20px" }}
              >
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Form. Bono Serie
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.formBonoSerie
                      ? practica?.practicaSempre.formBonoSerie
                      : "-"}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Form. Bono Número
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.formBonoNum}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Form. Bono Sub Número
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.formBonoSubNum
                      ? practica?.practicaSempre.formBonoSubNum
                      : "-"}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Form. Tipo
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {searchFormTipo(practica?.practicaSempre.formTipo)}
                  </Typography>
                </Grid>

                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Fecha Prescripción
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {getFormattedDate(
                      practica?.practicaSempre.fechaPrescripcion.toString()
                    )}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Fecha Autorización
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {getFormattedDate(
                      practica?.practicaSempre.fechaAutorizacion.toString()
                    )}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Número de Validación
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.numeroValidacion}
                  </Typography>
                </Grid>

                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Código Diagnóstico I
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.codigoDiagnosticoI
                      ? practica?.practicaSempre.codigoDiagnosticoI
                      : "-"}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Código Diagnóstico II
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.codigoDiagnosticoII
                      ? practica?.practicaSempre.codigoDiagnosticoII
                      : "-"}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Código Diagnóstico III
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.codigoDiagnosticoIII
                      ? practica?.practicaSempre.codigoDiagnosticoIII
                      : "-"}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Número Internación
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.internacionNumero
                      ? practica?.practicaSempre.internacionNumero
                      : "-"}
                  </Typography>
                </Grid>
              </Grid>
            )}

            <Grid container spacing={2} rowSpacing={4}>
              <Grid size={6}>
                <TextArea
                  name="observacion"
                  label="Observación"
                  register={register}
                  getFieldState={getFieldState}
                  formState={formState}
                  errors={errors}
                  required={false}
                  maxLength={250}
                  value={practica?.observacion}
                ></TextArea>
              </Grid>
            </Grid>
          </div>
        </Box>
      </div>
    </CustomModal>
  );
}

export default PracticaEditAdminModal;
